import { useEffect, useState } from "react";
import {
	FileField,
	FileInput,
	ImageField,
	ImageInput,
	BooleanInput,
	NumberInput,
	TextInput,
	RadioButtonGroupInput,
	useGetList,
	TabbedForm,
	FormTab,
	useTranslate,
} from "react-admin";
import { Button } from "@material-ui/core";
import { validateHandoutData } from "./handoutValidate";
import { transformToFormData } from "../../utils/customFunctions";
import { userPreferences } from "../../utils/fakeUserPrefrences";
import { CustomToolbar } from "../../customComponents/CustomToolbar";
import CustomCircularProgress from "../../customComponents/CustomCircularProgress";
import CustomImageField from "../../customComponents/CustomImageField";
import { getChoices } from "../modules/shared";

export const HandoutForm = ({
	isEdit = false,
	onSave,
	isLoading,
	handout = null,
}) => {
	const [languageData, setLanguageData] = useState([]);
	const [removeVideo, setRemoveVideo] = useState(false);
	const translate = useTranslate();

	const { data: languagesDataObj } = useGetList(
		"languages",
		{ page: 1, perPage: 10 },
		{ field: "id", order: "DESC" }
	);

	useEffect(() => {
		if (languagesDataObj) {
			const choices = getChoices(languagesDataObj);
			setLanguageData(choices);
		}
	}, [languagesDataObj]);

	const handleSave = (data) => {
		const dataWithRemoveVideo = {
			...data,
			removeVideo: removeVideo,
		};
		const transformed = transformToFormData(dataWithRemoveVideo);
		onSave(transformed);
	};

	return (
		<TabbedForm
			toolbar={<CustomToolbar loading={isLoading} />}
			validate={validateHandoutData}
			submitOnEnter={false}
			save={handleSave}
			syncWithLocation={false}
			validateOnBlur={true}
			warnWhenUnsavedChanges={false}
		>
			<FormTab label={translate("handouts.tabs.basicInfo.label")}>
				<CustomCircularProgress loading={isLoading} />
				<TextInput source="title" />
				<TextInput source="description" multiline={true} />

				{isEdit && handout?.imageCoverUrl && (
					<div style={{ marginBottom: "16px" }}>
						<label
							style={{
								display: "block",
								fontSize: "12px",
								color: "#666",
								marginBottom: "8px",
							}}
						>
							Current Cover Image:
						</label>
						<CustomImageField source="imageCoverUrl" />
					</div>
				)}

				<ImageInput
					source="imageCover"
					accept="image/*"
					label="Cover Image"
					placeholder="Upload Handout Cover Image"
				>
					<ImageField source="src" />
				</ImageInput>

				<RadioButtonGroupInput
					source="languageId"
					choices={languageData}
					optionText={(choice) => {
						if (
							choice &&
							choice.name &&
							typeof choice.name === "string"
						) {
							const translationKey = `misc.names.languages.${choice.name}`;

							try {
								const result = translate(translationKey);
								return result;
							} catch (error) {
								return choice.name;
							}
						}

						return choice?.name || choice?.id || "Unknown";
					}}
					optionValue="id"
				/>
				<BooleanInput
					source="isActive"
					label="Active"
					defaultValue={true}
				/>
				<NumberInput
					source="rank"
					label="Rank (optional)"
					min={1}
				/>
			</FormTab>

			<FormTab label={translate("handouts.tabs.manageHandouts.label")}>
				<FileInput
					source="handoutPdf"
					accept="application/pdf"
					label="Handout PDF"
					placeholder="Upload Handout PDF"
				>
					<FileField
						source="src"
						title="Handout PDF"
						target="_blank"
					/>
				</FileInput>

				{isEdit && handout?.handoutPdfUrl && (
					<div style={{ marginBottom: "16px" }}>
						<a
							href={handout.handoutPdfUrl}
							target="_blank"
							rel="noopener noreferrer"
							style={{
								color: "#1976d2",
								textDecoration: "underline",
							}}
						>
							View Current PDF
						</a>
					</div>
				)}


				<RadioButtonGroupInput
					source="videoOrientation"
					label="Video Orientation"
					choices={[
						{ id: "PORTRAIT", name: "Portrait" },
						{ id: "LANDSCAPE", name: "Landscape" },
					]}
					defaultValue="PORTRAIT"
				/>
				<FileInput
					source="handoutVideo"
					accept="video/*"
					label="Handout Video (Optional)"
					placeholder="Upload Handout Video"
				>
					<FileField
						source="src"
						title="Handout Video"
						target="_blank"
					/>
				</FileInput>

				{isEdit && handout?.handoutVideoUrl && !removeVideo && (
					<div
						style={{
							marginBottom: "16px",
							display: "flex",
							alignItems: "center",
							gap: "12px",
						}}
					>
						<div style={{ display: "flex", alignItems: "center" }}>
							<span
								style={{ color: "#4caf50", marginRight: "8px" }}
							>
								✓
							</span>
							<span style={{ fontSize: "14px", color: "#666" }}>
								Video Present
							</span>
						</div>
						<Button
							variant="outlined"
							color="secondary"
							size="small"
							onClick={() => setRemoveVideo(true)}
							style={{ fontSize: "12px", padding: "4px 8px" }}
						>
							Remove
						</Button>
					</div>
				)}

				<BooleanInput
					source="showInHandoutsSection"
					label="Show in 'Handouts' main section"
					defaultValue={false}
				/>
			</FormTab>
		</TabbedForm>
	);
};
